import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

const CATEGORIES = [
  { key: "subjects", label: "Subjects" },
  { key: "disciplines", label: "Disciplines" },
  { key: "reps", label: "Reps" },
  { key: "specialists", label: "District Reps / Specialists" },
];

function CategoryPanel({ category, readOnly }) {
  const [values, setValues] = useState([]);
  const [newVal, setNewVal] = useState("");
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = useCallback(() => {
    api.get("/options").then((r) => setValues(r.data[category] || [])).catch(() => {});
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const value = newVal.trim();
    if (!value) return;
    try {
      const { data } = await api.post("/options", { category, value });
      setValues(data.values);
      setNewVal("");
      toast.success(`Added “${value}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const saveEdit = async (old_value) => {
    const new_value = editVal.trim();
    if (!new_value || new_value === old_value) { setEditing(null); return; }
    try {
      const { data } = await api.put("/options", { category, old_value, new_value });
      setValues(data.values);
      setEditing(null);
      toast.success("Updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (value) => {
    try {
      const { data } = await api.delete("/options", { data: { category, value } });
      setValues(data.values);
      toast.success(`Removed “${value}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div>
      {!readOnly && (
      <div className="flex gap-2 mb-4">
        <Input
          data-testid={`option-add-input-${category}`}
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder={`Add a new ${category.replace(/s$/, "")}…`}
          className="rounded-sm h-9"
        />
        <Button data-testid={`option-add-btn-${category}`} onClick={add}
          className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-9 shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      )}
      <div className="border border-border rounded-sm divide-y divide-border max-h-[360px] overflow-y-auto">
        {values.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">No {category} yet.</div>
        )}
        {values.map((v) => (
          <div key={v} data-testid={`option-row-${category}-${v}`}
            className="flex items-center justify-between px-4 py-2 gap-2">
            {editing === v ? (
              <div className="flex items-center gap-2 flex-1">
                <Input autoFocus value={editVal} onChange={(e) => setEditVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(v); if (e.key === "Escape") setEditing(null); }}
                  className="rounded-sm h-8" data-testid={`option-edit-input-${category}`} />
                <Button size="sm" variant="ghost" className="h-8 rounded-sm text-green-600"
                  data-testid={`option-edit-save-${category}`} onClick={() => saveEdit(v)}>
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 rounded-sm text-muted-foreground"
                  onClick={() => setEditing(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <span className="text-sm">{v}</span>
                {!readOnly && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-8 rounded-sm"
                    data-testid={`option-edit-${category}-${v}`}
                    onClick={() => { setEditing(v); setEditVal(v); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 rounded-sm text-destructive"
                        data-testid={`option-delete-${category}-${v}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm bg-card">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove “{v}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          It will no longer appear as an option. Existing grievances using it are not changed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90"
                          onClick={() => remove(v)}>Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function OptionsManager({ readOnly = false }) {
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden mb-6" data-testid="manage-lists">
      <div className="px-5 py-3.5 border-b border-border">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Manage Lists</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {readOnly
            ? "View the Subjects, Disciplines and Reps used across grievance forms. Only the Master Admin can change these."
            : "Add, rename or remove Subjects, Disciplines and Reps used across grievance forms."}
        </p>
      </div>
      <div className="p-5">
        <Tabs defaultValue="subjects">
          <TabsList className="rounded-sm">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.key} value={c.key} data-testid={`manage-tab-${c.key}`} className="rounded-sm">{c.label}</TabsTrigger>
            ))}
          </TabsList>
          {CATEGORIES.map((c) => (
            <TabsContent key={c.key} value={c.key} className="mt-4">
              <CategoryPanel category={c.key} readOnly={readOnly} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
